import type { OnboardingChainStepState } from '@/types/onboarding-chain'

import { type SetupPosition, type SetupStepScreens, SETUP_STEP_SCREENS } from './setup-screen'

/** How the screen on show last changed, which picks the setup transition: `none`, `forward`, `back`, `step` or `land`. */
export type SetupMotion = 'none' | 'forward' | 'back' | 'step' | 'land'

/** Timings of the setup moments that run in script, in milliseconds. */
export const SETUP_MOTION_MS = {
  /** How long a finished read's closing line holds before the ring bursts. */
  ringHold: 500,
  /** How far into the burst the ring hands over to the next screen. */
  ringSettle: 150,
  /** How far into a confirm's success the screen starts to move on. */
  confirmHandoff: 360,
  /** The same under reduced motion: a 150 crossfade, then the 250 hold. */
  confirmHandoffReduced: 400,
  /** How long a screen that opens a step waits before it arrives; matches `--setup-arrival-delay`. */
  arrivalDelay: 80,
  /** How long one card takes to arrive. */
  cardArrival: 360,
  /** The gap between two cards that arrive one after another. */
  cardStagger: 40,
} as const

/** The done moment of a step, in milliseconds from the step turning done. */
export const STEP_DONE_MS = {
  numberFade: 120,
  checkDelay: 140,
  checkDraw: 260,
  lineDelay: 220,
  lineFill: 420,
  nextDelay: 620,
  nextLight: 280,
  flareDelay: 700,
  flare: 700,
  /** Every part under reduced motion: a plain crossfade. */
  reduced: 150,
} as const

function isTransient(position: SetupPosition, screens: SetupStepScreens): boolean {
  return screens[position.step][position.index]?.transient === true
}

/** The motion of a move between two screens of one step; leaving a read for a later screen is a landing. */
export function moveMotion(
  from: SetupPosition,
  to: SetupPosition,
  screens: SetupStepScreens = SETUP_STEP_SCREENS,
): SetupMotion {
  if (!isTransient(from, screens)) return 'forward'
  return to.index > from.index ? 'land' : 'back'
}

/** The motion of Continue from a step's last screen: a landing when that screen is a read. */
export function nextStepMotion(
  from: SetupPosition,
  screens: SetupStepScreens = SETUP_STEP_SCREENS,
): SetupMotion {
  return isTransient(from, screens) ? 'land' : 'step'
}

/** What the completion watcher reads of a step: its state and whether it was finished in this session. */
export interface StepProgress {
  readonly state: OnboardingChainStepState
  readonly fresh: boolean
}

/** The steps that turned done between two readings of the step list and were finished in this session, by index. */
export function freshlyDoneSteps(
  before: readonly OnboardingChainStepState[],
  after: readonly StepProgress[],
): number[] {
  return after.flatMap((entry, index) =>
    entry.state === 'done' && entry.fresh && before[index] !== 'done' ? [index] : [],
  )
}
